import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaAbsencesStepDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { absencesToOptions } from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import {
  A3InnunvaSource,
  A3InnuvaMapping,
  A3InnuvaWizardData,
} from 'modules/payroll_integrations_a3innuva/lib/types'

import { AbsenceItem } from './AbsenceItem'
import { TemporaryDisabilitiesToggle } from './TemporaryDisabilitiesToggle'

const a3InnuvaAbsenceSchema = z.object({
  mappings: z
    .array(
      z.object({
        factorial_id: z.string(),
        a3innuva_id: z.string(),
      })
    )
    .optional(),
})

const AbsencesStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()
  const absences: A3InnunvaSource | undefined = data.sources?.absences

  useUnsafeEffect(() => {
    if (data.sources?.absences === undefined) {
      const parsedData = a3InnuvaAbsenceSchema.safeParse(data.answers.absences)
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          absences: parsedData.success ? parsedData.data : {},
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: absencesData, loading } = useQuery(A3InnuvaAbsencesStepDocument, {
    variables: {
      companyIds: [session.company.id],
    },
  })

  const a3innuvaAbsenceTypes =
    absencesData?.payrollIntegrationsA3innuva.leaveTypesConnection.nodes ?? []
  const options = absencesToOptions(a3innuvaAbsenceTypes)
  const companyLeaves = absencesData?.timeoff.leaveTypesConnection.nodes ?? []
  const sortedLeaves = [...companyLeaves].sort((a, b) => {
    const aName = a.translatedName ?? a.name
    const bName = b.translatedName ?? b.name
    return aName.localeCompare(bName)
  })

  useOnNext(() => {
    trackEvent()
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        absences: JSON.parse(JSON.stringify(absences)),
      },
    })
  })

  const trackEvent = () => {
    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsAbsencesSelected, {
      company_id: session.company.id,
      access_id: session.access.id,
      absences: absences?.mappings?.map((absence) => absence.a3innuva_id),
    })
  }

  const updateMappings = (newMappings: A3InnuvaMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        absences: {
          mappings: newMappings,
        },
      },
    })
  }

  return loading || absences === undefined ? (
    <Spinner />
  ) : (
    <Box gap="s24">
      <Title
        title={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.title'
        )}
        description={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.description'
        )}
      />
      <TemporaryDisabilitiesToggle answers={data.answers} />
      <AbsenceItem
        companyLeaves={sortedLeaves}
        mappings={absences.mappings || []}
        options={options}
        onMappingChanged={updateMappings}
      />
    </Box>
  )
}

export const absencesStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <AbsencesStep {...props} />,
  isCompleted: (_data) => true,
})
